import React, { useState, useEffect } from 'react';
import supabase from '../../../../supabaseClient';
import { useAuth } from '../../../../contexts/authContext';
import { usePermissions } from '../../../../contexts/PermissionsContext';
import './CourseManagementPanel.css';

const CourseManagementPanel = ({ role, userId }) => {
  const { user } = useAuth();
  const { activeRole } = usePermissions();
  const [courses, setCourses] = useState([]);
  const [assignedCourses, setAssignedCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);

  const currentRole = activeRole || role;
  const currentUserId = user?.id || userId;
  const { roles } = usePermissions(); // Get all user roles to check for Designer

  useEffect(() => {
    if (currentUserId && currentRole) {
      fetchCourses();
    }
  }, [currentUserId, currentRole]);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      setError(null);

      let coursesQuery;
      let assignmentsQuery;

      // Use the improved view for courses with names
      coursesQuery = supabase
        .from('academy_courses_with_names')
        .select('*');

      // Role-specific filtering
      switch (currentRole) {
        case 'admin':
        case 'superadmin':
          // Admins see all courses (handled by RLS)
          break;
        case 'manager':
          // Managers see their assigned courses (handled by RLS)
          break;
        case 'teacher':
          // Teachers see their assigned courses (handled by RLS)
          break;
        default:
          throw new Error('Unauthorized role for course management');
      }

      const { data: coursesData, error: coursesError } = await coursesQuery;
      
      if (coursesError) throw coursesError;

      // Fetch course assignments for management roles
      if (['admin', 'superadmin', 'manager', 'teacher'].includes(currentRole)) {
        assignmentsQuery = supabase
          .from('academy_course_assignments')
          .select(`
            *,
            academy_courses(*),
            profiles:assigned_to(id, display_name, name, email)
          `)
          .eq('assigned_to', currentUserId);

        const { data: assignmentsData, error: assignmentsError } = await assignmentsQuery;
        
        if (assignmentsError) {
          console.warn('Error fetching assignments:', assignmentsError);
          // Don't throw here, assignments might not exist yet
        } else {
          setAssignedCourses(assignmentsData || []);
        }
      }

      setCourses(coursesData || []);
    } catch (err) {
      console.error('Error fetching courses:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const validateAndAssignCourse = async (courseId, teacherId, managerId) => {
    try {
      // Use the validation function we created
      const { data, error } = await supabase.rpc('validate_course_assignment', {
        course_uuid: courseId,
        teacher_uuid: teacherId,
        manager_uuid: managerId
      });

      if (error) throw error;

      // If validation passes, update the course
      const { error: updateError } = await supabase
        .from('academy_courses')
        .update({
          teacher_id: teacherId,
          manager_id: managerId
        })
        .eq('id', courseId);

      if (updateError) throw updateError;

      // Refresh courses
      await fetchCourses();
      setShowAssignModal(false);
      setSelectedCourse(null);
    } catch (err) {
      console.error('Error assigning course:', err);
      setError(err.message);
    }
  };

  const toggleCourseStatus = async (courseId, currentStatus) => {
    try {
      const { error } = await supabase
        .from('academy_courses')
        .update({ is_published: !currentStatus })
        .eq('id', courseId);

      if (error) throw error;
      await fetchCourses();
    } catch (err) {
      console.error('Error updating course status:', err);
      setError(err.message);
    }
  };

  const filteredCourses = courses.filter(course => {
    const matchesSearch = course.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         course.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Map is_published to status for filtering
    const courseStatus = course.is_published ? 'published' : 'draft';
    const matchesStatus = filterStatus === 'all' || courseStatus === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  const renderCourseCard = (course) => (
    <div key={course.id} className="course-card">
      <img
        src={course.thumbnail_url || '/default-course-thumbnail.jpg'}
        alt={course.title}
        className="course-thumbnail"
      />
      <div className="course-header">
        <h3>{course.title}</h3>
        <span className={`status-badge ${course.is_published ? 'published' : 'draft'}`}>
          {course.is_published ? 'published' : 'draft'}
        </span>
      </div>
      
      <div className="course-details">
        <p className="course-description">{course.description}</p>
        
        <div className="course-meta">
          <div className="meta-item">
            <span className="label">Language:</span>
            <span className="value">{course.language}</span>
          </div>
          <div className="meta-item">
            <span className="label">Level:</span>
            <span className="value">{course.level}</span>
          </div>
          <div className="meta-item">
            <span className="label">Duration:</span>
            <span className="value">{course.duration_weeks} weeks</span>
          </div>
          <div className="meta-item">
            <span className="label">Capacity:</span>
            <span className="value">{course.capacity} students</span>
          </div>
        </div>

        {course.teacher_name && (
          <div className="course-instructor">
            <span className="label">Teacher:</span>
            <span className="value">{course.teacher_name}</span>
          </div>
        )}

        {course.manager_name && (
          <div className="course-manager">
            <span className="label">Manager:</span>
            <span className="value">{course.manager_name}</span>
          </div>
        )}

        <div className="course-stats">
          <span className="enrollment-count">
            {course.enrollment_count || 0} enrolled
          </span>
          <span className="created-date">
            Created: {new Date(course.created_at).toLocaleDateString()}
          </span>
        </div>
      </div>

      <div className="course-actions">
        {(['admin', 'superadmin'].includes(currentRole)) && (
          <>
            <button 
              className="btn-primary"
              onClick={() => {
                setSelectedCourse(course);
                setShowAssignModal(true);
              }}
            >
              Assign
            </button>
            <button 
              className={`btn-${course.is_published ? 'warning' : 'success'}`}
              onClick={() => toggleCourseStatus(course.id, course.is_published)}
              title={course.is_published ? 'Unpublish Course' : 'Publish Course'}
            >
              {course.is_published ? 'Unpublish' : 'Publish'}
            </button>
          </>
        )}
        
        <button 
          className="btn-outline"
          onClick={() => window.open(`/app/academy/lms/courses/${course.id}`, '_blank')}
        >
          View Details
        </button>
      </div>
    </div>
  );

  const renderRoleSpecificContent = () => {
    switch (currentRole) {
      case 'admin':
      case 'superadmin':
        return (
          <div className="admin-course-management">
            <div className="management-stats">
              <div className="stat-card">
                <h4>Total Courses</h4>
                <span className="stat-number">{courses.length}</span>
              </div>
              <div className="stat-card">
                <h4>Published Courses</h4>
                <span className="stat-number">
                  {courses.filter(c => c.is_published === true).length}
                </span>
              </div>
              <div className="stat-card">
                <h4>Draft Courses</h4>
                <span className="stat-number">
                  {courses.filter(c => c.is_published === false || c.is_published === null).length}
                </span>
              </div>
              <div className="stat-card">
                <h4>Total Enrollments</h4>
                <span className="stat-number">
                  {courses.reduce((sum, course) => sum + (course.enrollment_count || 0), 0)}
                </span>
              </div>
            </div>
          </div>
        );

      case 'manager':
        return (
          <div className="manager-course-management">
            <div className="management-stats">
              <div className="stat-card">
                <h4>My Courses</h4>
                <span className="stat-number">{courses.length}</span>
              </div>
              <div className="stat-card">
                <h4>Active Students</h4>
                <span className="stat-number">
                  {courses.reduce((sum, course) => sum + (course.enrollment_count || 0), 0)}
                </span>
              </div>
            </div>
          </div>
        );

      case 'teacher':
        return (
          <div className="teacher-course-management">
            <div className="management-stats">
              <div className="stat-card">
                <h4>My Classes</h4>
                <span className="stat-number">{courses.length}</span>
              </div>
              <div className="stat-card">
                <h4>My Students</h4>
                <span className="stat-number">
                  {courses.reduce((sum, course) => sum + (course.enrollment_count || 0), 0)}
                </span>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="course-management-panel">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading courses...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="course-management-panel">
        <div className="error-state">
          <h3>Error Loading Courses</h3>
          <p>{error}</p>
          <button onClick={fetchCourses} className="btn-primary">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="course-management-panel">
      <div className="panel-header">
        <div className="header-content">
          <div className="header-text">
            <h2>Course Management</h2>
            <p>Manage and oversee course assignments and progress</p>
          </div>
          {(['admin', 'superadmin', 'manager', 'teacher'].includes(currentRole) && roles?.some(r => r.role === 'designer')) && (
            <div className="header-actions">
              <button 
                className="btn-primary"
                onClick={() => window.open('/app/academy/lms/designer/courses', '_blank')}
                title="Open Course Builder Studio (Designer access required)"
              >
                Create New Course
              </button>
            </div>
          )}
        </div>
      </div>

      {renderRoleSpecificContent()}

      <div className="search-filters">
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search courses..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        <div className="filter-controls">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Status</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </select>
        </div>
      </div>

      <div className="courses-grid">
        {filteredCourses.length > 0 ? (
          filteredCourses.map(renderCourseCard)
        ) : (
          <div className="empty-state">
            <h3>No courses found</h3>
            <p>No courses match your current filters.</p>
          </div>
        )}
      </div>

      {/* Assignment Modal would go here */}
      {showAssignModal && selectedCourse && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Assign Course: {selectedCourse.title}</h3>
            <p>Assignment functionality will be implemented here</p>
            <button onClick={() => setShowAssignModal(false)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseManagementPanel;
